import CustomGoogleMap from 'design-system/atoms/custom-google-map';
import { Box, Input, useSteps } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Marker } from '@react-google-maps/api';
import { useState } from 'react';
import { wktToPointArray } from '@lib/gis';
import FormField from 'design-system/molecules/form-field';
import LayoutMultiPanel from '../LayoutMultiPanel';
import Spinner from '../Spinner';
import WizardLayout, { WizardStepConfig } from '../WizardLayout';
import {
  useWorkAreasEditorManager,
  WorkAreasEditorMapLayer,
} from '../WorkAreasEditor';
import { EditWorkArea } from '../WorkAreasEditor/useWorkAreasEditorManager';
import {
  SegmentBusRoute,
  useSearchPublicBusData,
} from '../../data/useSearchPublicBusData';
import StepDrawArea from './StepDrawArea';
import { colorsSortedByKey } from '../../utils/routes';
import StepSelectRoutes from './StepSelectRoutes';
import { useContextSite } from '../../router/context/ContextSite';
import AnimatedRoute from '../AnimatedRoute';
import { getBusRouteStopIconUrl } from '../SitePublicTransport/BusRouteStopIcon';

export type BusMonitoringSegment = {
  name: string;
  areaWkt: string;
  busRouteSegments: {
    firstStopId: string;
    lastStopId: string;
    busRouteId: string;
  }[];
};
export interface BusRoutesSetupWizardProps {
  onSave: (busRoute: BusMonitoringSegment, searchArea: string) => Promise<void>;
  onCancel: () => void;
  initialMapBounds: google.maps.LatLngBoundsLiteral;
}

export default function BusRoutesSetupWizard({
  onSave,
  onCancel,
  initialMapBounds,
}: BusRoutesSetupWizardProps) {
  const { formatMessage } = useIntl();
  const stepsManager = useSteps();
  const [areaName, setAreaName] = useState('');
  const [searchArea, setSearchArea] = useState<EditWorkArea>();
  const [excludedRoutes, setExcludedRoutes] = useState<string[]>([]);
  const [stopsOverride, setStopsOverride] = useState<
    Record<string, { firstStopId?: string; lastStopId?: string }>
  >({});
  const [highlightedRoute, setHighlightedRoute] = useState<string | undefined>(
    undefined
  );
  const [hasSingleStop, setHasSingleStop] = useState<boolean>(false);
  const { siteId } = useContextSite();

  const workAreasEditorManager = useWorkAreasEditorManager([], {
    fillTransparency: 0.2,
  });
  const currentSearchArea =
    workAreasEditorManager.areaEditing?.wkt ?? searchArea?.wkt;
  const busRoutesDataHandler = useSearchPublicBusData(
    currentSearchArea
      ? {
          siteId,
          searchAreaWkt: currentSearchArea,
        }
      : null
  );
  const isLoading =
    busRoutesDataHandler.data === undefined &&
    busRoutesDataHandler.isValidating;
  const busRoutes = busRoutesDataHandler.data ?? [];
  const routeColors = colorsSortedByKey(busRoutes, 'routeId');
  const configuredBusRoutes = busRoutes
    .filter((route) => !excludedRoutes.includes(route.routeId))
    .map((route) => ({
      ...route,
      ...stopsOverride[route.routeId],
    }));
  const allRoutesExcluded =
    busRoutes.length > 0 && excludedRoutes.length === busRoutes.length;
  const steps: WizardStepConfig[] = [
    {
      name: formatMessage({
        defaultMessage: 'Monitoring details',
        id: 'EGU+e2',
      }),
      title: formatMessage({
        defaultMessage:
          'Enter a name that helps identify the monitored bus routes',
        id: 'ROGtAR',
      }),
      content: (
        <Box>
          <FormField
            labelText={
              <FormattedMessage
                defaultMessage="Monitoring area name"
                id="jXWTRe"
              />
            }
          >
            <Input
              value={areaName}
              onChange={(e) => {
                setAreaName(e.target.value);
              }}
              size="sm"
            />
          </FormField>
        </Box>
      ),
      isProgressDisabled: areaName.length === 0,
    },
    {
      name: formatMessage({
        defaultMessage: 'Search area',
        id: 'J9dhoi',
      }),
      title: formatMessage({
        defaultMessage: 'Draw an area to select routes from',
        id: 'qmx7b0',
      }),
      onStepEntered: () => {
        if (searchArea) {
          workAreasEditorManager.onEditingChange(searchArea);
          setSearchArea(undefined);
          setExcludedRoutes([]);
        } else {
          workAreasEditorManager.polygonCreatorManager.startCreating();
        }
      },
      onStepWillExit: () => {
        if (!workAreasEditorManager.areaEditing) {
          return 'PREVENT_WIZARD_STEP_EXIT';
        }
        setSearchArea({
          id: 'user-search-area',
          ...workAreasEditorManager.areaEditing,
        });
        if (workAreasEditorManager.polygonCreatorManager.isCreating) {
          workAreasEditorManager.polygonCreatorManager.stopCreating();
        }
        if (workAreasEditorManager.areaEditing) {
          workAreasEditorManager.cancelEditing();
        }
        return undefined;
      },
      isProgressDisabled:
        !workAreasEditorManager.areaEditing || busRoutes.length === 0,
      content: (
        <StepDrawArea
          isCreating={workAreasEditorManager.polygonCreatorManager.isCreating}
          onStartCreating={() => {
            setSearchArea(undefined);
            workAreasEditorManager.polygonCreatorManager.startCreating();
          }}
          busRoutes={busRoutes}
          routeColors={routeColors}
        />
      ),
    },
    {
      name: formatMessage({
        defaultMessage: 'Select routes',
        id: '8ks+D9',
      }),
      title: formatMessage({
        defaultMessage: 'Select routes from your drawn area',
        id: 'GqbqSG',
      }),
      content: (
        <StepSelectRoutes
          busRoutes={busRoutes}
          routeColors={routeColors}
          excludedRoutes={excludedRoutes}
          highlightedRoute={highlightedRoute}
          onSelectedRoutesChange={setExcludedRoutes}
          onRouteHover={setHighlightedRoute}
          stopsOverride={stopsOverride}
          onStopsOverrideChange={setStopsOverride}
          onHasSingleStopChange={setHasSingleStop}
        />
      ),
      isProgressDisabled: configuredBusRoutes.length === 0,
    },
  ];

  return (
    <LayoutMultiPanel
      contentSidebar={
        <WizardLayout
          stepsManager={stepsManager}
          onCancel={onCancel}
          onExit={
            stepsManager.activeStep !== steps.length - 1
              ? undefined
              : {
                  action: () =>
                    onSave(
                      {
                        name: areaName,
                        areaWkt: searchArea?.wkt ?? '',
                        busRouteSegments: configuredBusRoutes.map((route) => ({
                          firstStopId: route.firstStopId,
                          lastStopId: route.lastStopId,
                          busRouteId: route.busServiceId,
                        })),
                      },
                      searchArea?.wkt ?? ''
                    ),
                  label: formatMessage({
                    defaultMessage: 'Create monitoring area',
                    id: 'K748iG',
                  }),
                }
          }
          steps={steps}
          onExitDisabled={hasSingleStop || allRoutesExcluded}
        />
      }
      contentMain={
        <Box width="100%" height="100%" position="relative">
          {isLoading && (
            <Box
              width="100%"
              height="100%"
              position="absolute"
              top={0}
              left={0}
              backgroundColor="whiteAlpha.700"
              zIndex={1}
            >
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
              >
                <Spinner size="lg" />
              </Box>
            </Box>
          )}
          <CustomGoogleMap
            enableScrollZoom
            setBoundsToElements={[
              { lat: initialMapBounds.north, lng: initialMapBounds.west },
              { lat: initialMapBounds.south, lng: initialMapBounds.east },
            ]}
          >
            <MapLayerBusStopsSelection
              busRoutes={configuredBusRoutes}
              routeColors={routeColors}
              highlightedRoute={highlightedRoute}
            />
            <WorkAreasEditorMapLayer
              workAreas={searchArea ? [searchArea] : []}
              workAreasEditorManager={workAreasEditorManager}
            />
          </CustomGoogleMap>
        </Box>
      }
      layoutSettings={{
        addPadding: false,
        showLogo: false,
      }}
    />
  );
}

function MapLayerBusStopsSelection({
  busRoutes,
  routeColors,
  highlightedRoute,
}: {
  busRoutes: SegmentBusRoute[];
  routeColors: Record<string, string>;
  highlightedRoute: string | undefined;
}) {
  const selectedBusStops = busRoutes
    .filter(
      (route) =>
        highlightedRoute === undefined || highlightedRoute === route.routeId
    )
    .flatMap((route) => {
      const firstStop = route.stops.find((s) => s.stopId === route.firstStopId);
      const lastStop = route.stops.find((s) => s.stopId === route.lastStopId);
      const routeColor = routeColors[route.routeId];
      if (!firstStop || !lastStop) {
        return [];
      }
      return [
        {
          key: `${route.routeId}-first-${firstStop.stopId}`,
          ...firstStop,
          routeColor,
          routes: [route],
          stopType: 'origin' as const,
        },
        {
          key: `${route.routeId}-last-${lastStop.stopId}-`,
          ...lastStop,
          routeColor,
          routes: [route],
          stopType: 'destination' as const,
        },
      ];
    });
  return (
    <>
      {busRoutes.map((busRoute) => (
        <AnimatedRoute
          key={busRoute.routeId}
          routePoints={wktToPointArray(busRoute.wkt)}
          routeColor={routeColors[busRoute.routeId]}
          isHighlighted={highlightedRoute === busRoute.routeId}
          shouldAnimate={highlightedRoute === busRoute.routeId}
          zIndex={highlightedRoute === busRoute.routeId ? 1 : 0}
        />
      ))}
      {selectedBusStops.map((stop) => (
        <Marker
          key={stop.key}
          position={{ lat: stop.lat, lng: stop.lon }}
          title={stop.stopName}
          zIndex={2}
          icon={{
            url: getBusRouteStopIconUrl({
              stopType: stop.stopType,
              boxSize: 32,
              color: stop.routeColor,
            }),
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 16),
          }}
        />
      ))}
    </>
  );
}
